import { useCallback, useContext } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { EncryptedPrivateKey, GenerateKey } from '@sorare/wallet-shared';

import { useAuthContext } from '#src/contexts/auth/index.ts';
import type { SignedInInfo } from '#src/contexts/connection/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { nullAddress } from '#src/lib/ethereum.ts';
import { mustAcceptTermsOfServiceFlag } from '#src/lib/mustAcceptTermsOfServiceFlag.ts';

export const useOnSignedIn = () => {
  const { sendRequest } = useContext(MessagingContext)!;
  const { updateUser } = useAuthContext();
  const { promptTerms } = useConnectionContext();
  const {
    flags: { lastTermsOfServiceUpdatedAt = '' },
  } = useFeatureFlags();

  const mustAcceptTermsFlag = mustAcceptTermsOfServiceFlag(
    lastTermsOfServiceUpdatedAt
  );

  const onSignedIn = useCallback(
    async (
      {
        email,
        wallet,
        sorarePrivateKey,
        mustAcceptTcus,
        ...rest
      }: SignedInInfo,
      passwordHash?: string
    ) => {
      let address = wallet?.ethereumAddress;
      let userPrivateKey = sorarePrivateKey;

      if (!address || address === nullAddress) {
        const { result } = await sendRequest<GenerateKey>('generateKey', {
          email,
        });

        if (result) {
          const { wallet: newWallet } = result;
          const {
            ethereumAddress,
            passwordEncryptedPrivateKey: { iv, salt, payload },
          } = newWallet;

          address = ethereumAddress;
          userPrivateKey = {
            iv,
            salt,
            encryptedPrivateKey: payload,
          };

          await updateUser({
            currentPasswordHash: passwordHash,
            ...newWallet,
          });
        }
      }
      if (mustAcceptTcus && mustAcceptTermsFlag) {
        promptTerms({
          closable: true,
        });
      }

      return {
        result: {
          ...rest,
          address: address as string,
          userPrivateKey: userPrivateKey as EncryptedPrivateKey,
          email,
        },
      };
    },
    [sendRequest, updateUser, promptTerms, mustAcceptTermsFlag]
  );
  return onSignedIn;
};
